/**
 * Copy-to-clipboard with a transient `isCopied` flag — shared copy plumbing (Kelly AI message copy; the
 * artifact-panel copy button reuses it at APP-3359). Prefers the async Clipboard API and falls back to a
 * hidden-textarea `execCommand` for insecure contexts / older browsers.
 *
 * Supersedes `helpers/clipboard.ts` for new code (that helper has no fallback and isn't a hook); its three
 * existing call sites are intentionally left unmigrated.
 */

import { useCallback, useEffect, useRef, useState } from "react"

async function writeClipboardText(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      return true
    } catch {
      // Clipboard API can reject (permissions, insecure context) — fall through to the legacy path.
    }
  }
  if (typeof document === "undefined") return false
  // Preserve focus: the textarea dance steals it, which would collapse a `:focus-within` reveal the trigger lives in.
  const previouslyFocused = document.activeElement as HTMLElement | null
  const textarea = document.createElement("textarea")
  textarea.value = text
  textarea.setAttribute("readonly", "")
  textarea.style.position = "fixed"
  textarea.style.opacity = "0"
  document.body.appendChild(textarea)
  textarea.select()
  let didCopy = false
  try {
    didCopy = document.execCommand("copy")
  } catch {
    didCopy = false
  }
  document.body.removeChild(textarea)
  previouslyFocused?.focus?.()
  return didCopy
}

export interface UseCopyToClipboardResult {
  /** Copies `text`; resolves true on success. Flips `isCopied` for `resetMs` on success. */
  copy: (text: string) => Promise<boolean>
  isCopied: boolean
}

export function useCopyToClipboard(resetMs = 1500): UseCopyToClipboardResult {
  const [isCopied, setIsCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const isMountedRef = useRef(false)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      clearTimeout(timerRef.current)
    }
  }, [])

  const copy = useCallback(
    async (text: string) => {
      const didCopy = await writeClipboardText(text)
      // Guard the post-await state/timer against an unmount during the async write.
      if (didCopy && isMountedRef.current) {
        setIsCopied(true)
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => setIsCopied(false), resetMs)
      }
      return didCopy
    },
    [resetMs],
  )

  return { copy, isCopied }
}
